import { create } from 'zustand'
import type { ModuleName, StrategyName } from '@/types'

/**
 * Persist the active module so a hard reload doesn't yank the user back to
 * the dashboard. v0.8.23: prior versions kept `activeModule` in-memory only,
 * so every refresh — and the routing pattern's frequent `window.location`
 * navigations — landed the user on the dashboard regardless of where they
 * were last working. localStorage keyed under a versioned key so a future
 * shape change (workspace-scoped, multi-tab synced, etc.) can migrate cleanly.
 * Failures (private mode, quota) are absorbed: the user just defaults to
 * dashboard on next load, which is the safe degraded behavior.
 *
 * Mirrors `activeMonth` persistence in `ledgerStore`.
 */
const ACTIVE_MODULE_STORAGE_KEY = 'debtos:activeModule:v1'

const VALID_MODULES: readonly ModuleName[] = [
  'dashboard',
  'registry',
  'engine',
  'cashflow',
  'journey',
  'relief',
  'sandbox',
] as const

function isValidModule(v: unknown): v is ModuleName {
  return typeof v === 'string' && (VALID_MODULES as readonly string[]).includes(v)
}

function readPersistedActiveModule(): ModuleName | null {
  try {
    const raw = localStorage.getItem(ACTIVE_MODULE_STORAGE_KEY)
    if (!raw) return null
    // Shape check — must be one of the known module names, else fall back to
    // the default. Prevents a stale storage value from a removed module from
    // hanging the app on a missing route.
    return isValidModule(raw) ? raw : null
  } catch {
    return null
  }
}

function writePersistedActiveModule(module: ModuleName): void {
  try {
    localStorage.setItem(ACTIVE_MODULE_STORAGE_KEY, module)
  } catch {
    // Private browsing / quota — non-fatal. Next load defaults to dashboard.
  }
}

interface UIState {
  activeModule: ModuleName
  selectedStrategy: StrategyName
  extraMonthlyPayment: number
  setActiveModule: (module: ModuleName) => void
  setSelectedStrategy: (strategy: StrategyName) => void
  setExtraMonthlyPayment: (amount: number) => void
}

export const useUIStore = create<UIState>()((set) => ({
  activeModule: readPersistedActiveModule() ?? 'dashboard',
  selectedStrategy: 'avalanche',
  extraMonthlyPayment: 0,
  setActiveModule: (module) => {
    set({ activeModule: module })
    writePersistedActiveModule(module)
  },
  setSelectedStrategy: (strategy) => set({ selectedStrategy: strategy }),
  setExtraMonthlyPayment: (amount) => set({ extraMonthlyPayment: amount }),
}))
